// TEMPLATES-T1..T4 row contracts and jsonb shapes (docs/design/2026-10-28-agent-templates.md §1,
// §6). Same conventions as `rows.ts`: keys are column names, nullability is the column's, enums
// are the CHECK's; bound in `contract-bindings.ts`. The handlers validate with the domain shapes
// (`@companyos/domain-agents/templates`), whose stored form is exactly this snake_case JSON.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AccessDefaultSource,
  AccessFamily,
  AccessLevel,
  ModelTier,
  TemplateOverlayScope,
  VerticalPackStatus,
} from "./enums";
import { boundedRecord, LIMITS, Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

const Level4 = z.enum(["R", "A", "D", "M"]);
const PolicyMode = z.enum(["direct", "ask", "never"]);

export const RoleRequiredConnection = z
  .object({ family: AccessFamily, why: z.string().max(LIMITS.text) })
  .meta({ id: "RoleRequiredConnection" });
export type RoleRequiredConnection = z.infer<typeof RoleRequiredConnection>;

const Key = z.string().max(LIMITS.key);

export const RoleAccessFamilyDefault = z
  .object({
    level: Level4,
    actions: boundedRecord(z.enum(["R", "A", "D", "M", "never"]), 32, Key).optional(),
    after_probation: boundedRecord(z.enum(["A", "D"]), 32, Key).optional(),
    limits: z
      .object({
        daily_acts: z.int().optional(),
        recipient_domains: z.array(z.string().max(253)).max(LIMITS.list).optional(),
      })
      .optional(),
  })
  .meta({ id: "RoleAccessFamilyDefault" });

export const RoleAccessDefaults = z
  .object({
    families: z
      .partialRecord(AccessFamily, RoleAccessFamilyDefault)
      .meta({ maxProperties: AccessFamily.options.length }),
    classes: boundedRecord(PolicyMode, LIMITS.recordKeys, Key),
  })
  .meta({ id: "RoleAccessDefaults" });
export type RoleAccessDefaults = z.infer<typeof RoleAccessDefaults>;

export const RoleKpiRow = z
  .object({ key: Key, target: z.number().nullable(), direction: z.enum(["gte", "lte"]) })
  .meta({ id: "RoleKpi" });
export type RoleKpiRow = z.infer<typeof RoleKpiRow>;

export const RoleCadenceRow = z
  .object({
    key: Key,
    name: z.string().max(LIMITS.name),
    rrule: z.string().max(500),
    skill: Key.optional(),
  })
  .meta({ id: "RoleCadence" });
export type RoleCadenceRow = z.infer<typeof RoleCadenceRow>;

export const RoleProbation = z
  .object({ tasks: z.int().min(0), days: z.int().min(0) })
  .meta({ id: "RoleProbation" });

/** A role template version's defaults as an author sends them (agency console, DC-03). */
export const RoleDefaultsInput = z
  .strictObject({
    required_connections: z.array(RoleRequiredConnection).max(12),
    access_defaults: RoleAccessDefaults,
    kpis: z.array(RoleKpiRow).max(16),
    cadence: z.array(RoleCadenceRow).max(16),
    model_tier: ModelTier,
    budget_default_minor: z.int().min(0).max(100_000_000),
    probation: RoleProbation,
  })
  .meta({ id: "RoleDefaultsInput" });
export type RoleDefaultsInput = z.infer<typeof RoleDefaultsInput>;

/** Family → ceiling (`none` closes it). */
export const AccessCapsJson = z
  .partialRecord(AccessFamily, AccessLevel)
  .meta({ id: "AccessCaps", maxProperties: AccessFamily.options.length });
export type AccessCapsJson = z.infer<typeof AccessCapsJson>;
export const KpiOverridesJson = z
  .record(z.string(), z.number().nullable())
  .meta({ id: "KpiOverrides" });
export type KpiOverridesJson = z.infer<typeof KpiOverridesJson>;

export const TemplateOverlay = z
  .object({
    company_id: Uuid,
    id: Uuid,
    scope: TemplateOverlayScope,
    template_key: z.string().nullable(),
    vertical_pack_id: Uuid.nullable(),
    name: z.string(),
    context_text: z.string().nullable(),
    tone_text: z.string().nullable(),
    add_skill_keys: z.array(z.string()),
    block_skill_keys: z.array(z.string()),
    access_caps: AccessCapsJson,
    blocked_actions: z.array(z.string()),
    kpi_overrides: KpiOverridesJson,
    model_tier: ModelTier.nullable(),
    budget_minor: z.int().min(0).nullable(),
    version: z.int().min(1),
    archived_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "TemplateOverlay" });
export type TemplateOverlay = z.infer<typeof TemplateOverlay>;

export const DeAccessDefault = z
  .object({
    company_id: Uuid,
    de_membership_id: Uuid,
    family: AccessFamily,
    level: AccessLevel,
    role_level: AccessLevel.nullable(),
    source: AccessDefaultSource,
    confirmed_by_membership_id: Uuid.nullable(),
    confirmed_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "DeAccessDefault" });
export type DeAccessDefault = z.infer<typeof DeAccessDefault>;

export const TemplateVerticalPack = z
  .object({
    id: Uuid,
    key: z.string(),
    name: z.string(),
    description: z.string(),
    version: z.int().min(1),
    base_template_keys: z.array(z.string()),
    add_skill_keys: z.array(z.string()),
    context_text: z.string().nullable(),
    tone_text: z.string().nullable(),
    access_caps: AccessCapsJson,
    blocked_actions: z.array(z.string()),
    kpi_overrides: KpiOverridesJson,
    model_tier: ModelTier.nullable(),
    budget_minor: z.int().min(0).nullable(),
    status: VerticalPackStatus,
    published_at: Timestamp,
    deprecated_at: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "TemplateVerticalPack" });
export type TemplateVerticalPack = z.infer<typeof TemplateVerticalPack>;
